import { supabase } from './supabase';
import { EmailService } from './email-service';

export interface StorageUsageInfo {
  currentUsageBytes: number;
  maxStorageBytes: number;
  percentageUsed: number;
  remainingBytes: number;
  formattedUsage: string;
  formattedMax: string;
  formattedRemaining: string;
}

export interface StorageCheckResult {
  allowed: boolean;
  reason?: string;
  currentUsage?: StorageUsageInfo;
  requiredUpgrade?: string;
}

export class StorageLimitService {
  /**
   * Format bytes to human-readable string
   */
  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Calculate total storage used by a user
   */
  static async calculateUserStorageUsage(userId: string): Promise<number> {
    try {

      // Get all creatives for the user
      const { data: creatives, error } = await supabase
        .from('creatives')
        .select('file_size')
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error fetching creatives for storage calculation:', error);
        throw new Error('Failed to calculate storage usage');
      }

      // Sum up all file sizes
      const totalBytes = creatives?.reduce((sum, creative) => {
        return sum + (creative.file_size || 0);
      }, 0) || 0;

      return totalBytes;
    } catch (error) {
      console.error('❌ Error calculating storage usage:', error);
      throw error;
    }
  }

  /**
   * Update user's current storage usage in the database
   */
  static async updateStorageUsage(userId: string, newUsageBytes: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('subscription_limits')
        .update({ current_storage_used: newUsageBytes })
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error updating storage usage:', error);
        // Don't throw - this is for tracking only
      }
    } catch (error) {
      console.error('❌ Error updating storage usage:', error);
    }
  }

  /**
   * Get user's storage limit based on their subscription plan
   */
  static async getUserStorageLimit(userId: string): Promise<number> {
    try {
      // Get user's subscription plan
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('subscription_plan')
        .eq('id', userId)
        .single();

      if (userError || !user) {
        console.error('❌ Error fetching user:', userError);
        // Default to free trial limit
        return 1073741824; // 1 GB
      }

      const planId = user.subscription_plan || 'trial';

      // Get storage limit for the plan
      const { data: plan, error: planError } = await supabase
        .from('subscription_plans')
        .select('max_storage_bytes')
        .eq('id', planId)
        .single();

      if (planError || !plan || !plan.max_storage_bytes) {
        console.error('❌ Error fetching plan storage limit:', planError);
        // Default to free trial limit
        return 1073741824; // 1 GB
      }

      return plan.max_storage_bytes;
    } catch (error) {
      console.error('❌ Error getting storage limit:', error);
      // Default to free trial limit
      return 1073741824; // 1 GB
    }
  }

  /**
   * Get comprehensive storage usage information for a user
   */
  static async getStorageUsageInfo(userId: string): Promise<StorageUsageInfo> {
    try {
      const [currentUsageBytes, maxStorageBytes] = await Promise.all([
        this.calculateUserStorageUsage(userId),
        this.getUserStorageLimit(userId)
      ]);

      const remainingBytes = Math.max(0, maxStorageBytes - currentUsageBytes);
      const percentageUsed = maxStorageBytes > 0 
        ? Math.round((currentUsageBytes / maxStorageBytes) * 100) 
        : 0;

      return {
        currentUsageBytes,
        maxStorageBytes,
        percentageUsed,
        remainingBytes,
        formattedUsage: this.formatBytes(currentUsageBytes),
        formattedMax: this.formatBytes(maxStorageBytes),
        formattedRemaining: this.formatBytes(remainingBytes)
      };
    } catch (error) {
      console.error('❌ Error getting storage usage info:', error);
      throw error;
    }
  }

  /**
   * Check if a user can upload a file of a given size
   */
  static async canUploadFile(userId: string, fileSizeBytes: number): Promise<StorageCheckResult> {
    try {

      const usageInfo = await this.getStorageUsageInfo(userId);
      
      // Check if the new file would exceed the limit
      const newTotalBytes = usageInfo.currentUsageBytes + fileSizeBytes;
      
      if (newTotalBytes > usageInfo.maxStorageBytes) {
        console.log(`❌ Upload would exceed storage limit: ${this.formatBytes(newTotalBytes)} > ${usageInfo.formattedMax}`);
        
        // Determine suggested upgrade plan
        let requiredUpgrade = 'starter';
        if (usageInfo.maxStorageBytes >= 16106127360) requiredUpgrade = 'pro'; // Already on starter
        if (usageInfo.maxStorageBytes >= 53687091200) requiredUpgrade = 'agency'; // Already on pro
        if (usageInfo.maxStorageBytes >= 214748364800) requiredUpgrade = 'enterprise'; // Already on agency

        return {
          allowed: false,
          reason: `Storage limit exceeded. You are currently using ${usageInfo.formattedUsage} of your ${usageInfo.formattedMax} limit. This file (${this.formatBytes(fileSizeBytes)}) would put you over the limit. Please upgrade your plan or delete some files.`,
          currentUsage: usageInfo,
          requiredUpgrade
        };
      }

      console.log(`✅ Upload allowed. Current: ${usageInfo.formattedUsage}, New total: ${this.formatBytes(newTotalBytes)}, Limit: ${usageInfo.formattedMax}`);
      
      return {
        allowed: true,
        currentUsage: usageInfo
      };
    } catch (error) {
      console.error('❌ Error checking storage limit:', error);
      // In case of error, allow the upload but log the issue
      return {
        allowed: true,
        reason: 'Could not verify storage limit'
      };
    }
  }

  /**
   * Record file upload and update storage usage
   */
  static async recordFileUpload(userId: string, fileSizeBytes: number): Promise<void> {
    try {
      // Recalculate and update storage usage
      const newUsage = await this.calculateUserStorageUsage(userId);
      await this.updateStorageUsage(userId, newUsage);
      
      console.log(`📝 Recorded file upload: ${this.formatBytes(fileSizeBytes)}, new total: ${this.formatBytes(newUsage)}`);
      
      // Check for storage warnings and send email notifications
      await this.checkAndSendStorageWarning(userId);
    } catch (error) {
      console.error('❌ Error recording file upload:', error);
      // Don't throw - this is for tracking only
    }
  }

  /**
   * Record file deletion and update storage usage
   */
  static async recordFileDeletion(userId: string, fileSizeBytes: number): Promise<void> {
    try {
      // Recalculate and update storage usage
      const newUsage = await this.calculateUserStorageUsage(userId);
      await this.updateStorageUsage(userId, newUsage);
      
      console.log(`🗑️ Recorded file deletion: ${this.formatBytes(fileSizeBytes)}, new total: ${this.formatBytes(newUsage)}`);
    } catch (error) {
      console.error('❌ Error recording file deletion:', error);
      // Don't throw - this is for tracking only
    }
  }

  /**
   * Check and send storage warning emails
   */
  private static async checkAndSendStorageWarning(userId: string): Promise<void> {
    try {
      // Get user's storage limit
      const usageInfo = await this.getStorageUsageInfo(userId);
      const percentageUsed = usageInfo.percentageUsed;
      
      // Get user details for email
      const { data: userData } = await supabase
        .from('users')
        .select('name, email')
        .eq('id', userId)
        .single();
      
      if (!userData || !userData.email) {
        console.log('No user data or email found for storage warning');
        return;
      }
      
      // Check if we should send a warning (80% threshold)
      if (percentageUsed >= 80 && percentageUsed < 100) {
        // Check if we've already sent a warning recently
        const { data: lastWarning } = await supabase
          .from('email_logs')
          .select('created_at')
          .eq('user_id', userId)
          .eq('template', 'storage-warning')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        // Only send if we haven't sent a warning in the last 7 days
        const lastWarningDate = lastWarning && lastWarning.created_at ? new Date(lastWarning.created_at) : null;
        const daysSinceLastWarning = lastWarningDate 
          ? (Date.now() - lastWarningDate.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;
        
        if (daysSinceLastWarning > 7) {
          console.log('📧 Sending storage warning email...');
          
          await EmailService.send({
            to: userData.email,
            template: 'storage-warning',
            data: {
              userName: userData.name || 'there',
              storageUsed: usageInfo.formattedUsage,
              storageLimit: usageInfo.formattedMax,
              percentageUsed: Math.round(percentageUsed),
              upgradeUrl: `${import.meta.env.VITE_APP_URL || 'http://localhost:5173'}/credits`
            },
            userId: userId
          });
          
          console.log('✅ Storage warning email sent successfully');
        }
      }
      
      // Check if storage is full (95% or more - leaving some buffer)
      if (percentageUsed >= 95) {
        // Check if we've already sent a storage full email recently
        const { data: lastFullNotice } = await supabase
          .from('email_logs')
          .select('created_at')
          .eq('user_id', userId)
          .eq('template', 'storage-full')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        const lastFullDate = lastFullNotice && lastFullNotice.created_at ? new Date(lastFullNotice.created_at) : null;
        const daysSinceLastFull = lastFullDate 
          ? (Date.now() - lastFullDate.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;
        
        if (daysSinceLastFull > 30) {
          console.log('📧 Sending storage full email...');
          
          await EmailService.send({
            to: userData.email,
            template: 'storage-full',
            data: {
              userName: userData.name || 'there',
              storageUsed: usageInfo.formattedUsage,
              storageLimit: usageInfo.formattedMax,
              upgradeUrl: `${import.meta.env.VITE_APP_URL || 'http://localhost:5173'}/credits`
            },
            userId: userId
          });
          
          console.log('✅ Storage full email sent successfully');
        }
      }
    } catch (error) {
      console.error('❌ Error checking/sending storage warning:', error);
      // Don't throw - this shouldn't break the storage tracking flow
    }
  }
}

export default StorageLimitService;